
import { useMemo } from "react";
import { 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  format, 
  isSameMonth,
  isToday,
  parseISO
} from "date-fns";
import { Badge } from "@/components/ui/badge";
import { CalendarContentItem } from "@/hooks/useContentCalendar";
import { cn } from "@/lib/utils";
import { FileText, Linkedin } from "lucide-react";

interface CalendarWeekViewProps {
  currentDate: Date;
  items: CalendarContentItem[];
  onItemClick: (item: CalendarContentItem) => void;
}

export const CalendarWeekView = ({ currentDate, items, onItemClick }: CalendarWeekViewProps) => {
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate);
    const weekEnd = endOfWeek(currentDate);
    
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate]);
  
  // Group items by day
  const itemsByDay = useMemo(() => {
    const grouped: Record<string, CalendarContentItem[]> = {};
    
    items.forEach(item => {
      let dateKey: string | null = null;
      
      // Try scheduledFor date first
      if (item.scheduledFor) {
        dateKey = format(new Date(item.scheduledFor), "yyyy-MM-dd");
      } 
      // Fall back to publishedAt if available
      else if (item.publishedAt) {
        dateKey = format(new Date(item.publishedAt), "yyyy-MM-dd");
      }
      
      if (dateKey) {
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        
        // Check for duplicates
        const isDuplicate = grouped[dateKey].some(existingItem => 
          existingItem.id === item.id && existingItem.sourceType === item.sourceType);
        
        if (!isDuplicate) {
          grouped[dateKey].push(item);
        }
      }
    });
    
    return grouped;
  }, [items]);
  
  // Get items for a specific day
  const getItemsForDay = (day: Date): CalendarContentItem[] => {
    const dateKey = format(day, "yyyy-MM-dd");
    return itemsByDay[dateKey] || [];
  };
  
  // Get content type icon
  const getContentTypeIcon = (item: CalendarContentItem) => {
    if (item.sourceType === 'blog') {
      return <FileText className="w-3 h-3 mr-1 text-blue-600" />;
    } else {
      return <Linkedin className="w-3 h-3 mr-1 text-blue-700" />;
    }
  };
  
  return (
    <div className="grid grid-cols-7 gap-1">
      {weekDays.map((day, index) => {
        const isCurrentMonth = isSameMonth(day, currentDate);
        const itemsForDay = getItemsForDay(day);
        const dayIsToday = isToday(day);
        
        return (
          <div 
            key={index} 
            className={cn(
              "h-64 p-2 border rounded-sm overflow-hidden",
              !isCurrentMonth && "bg-slate-50 border-slate-100",
              isCurrentMonth && "border-slate-200",
              dayIsToday && "bg-blue-50 border-blue-200"
            )}
          >
            <div className="flex justify-between items-start">
              <span className={cn(
                "text-sm font-semibold",
                !isCurrentMonth && "text-slate-400",
                dayIsToday && "text-blue-600"
              )}>
                {format(day, "EEE")}, {format(day, "d")}
              </span>
              
              {itemsForDay.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {itemsForDay.length}
                </Badge>
              )}
            </div>
            
            <div className="mt-2 space-y-2 overflow-y-auto max-h-[calc(100%-24px)]">
              {itemsForDay.length === 0 ? (
                <div className="text-xs text-slate-400 py-2 px-1">
                  No content scheduled
                </div>
              ) : (
                itemsForDay.map((item) => {
                  // Display date - prefer scheduled_for for upcoming/scheduled content
                  const displayDate = item.status === 'published' && item.publishedAt
                    ? item.publishedAt
                    : item.scheduledFor;
                  
                  return (
                    <div 
                      key={`${item.sourceType}-${item.id}`} 
                      className={cn(
                        "text-xs p-2 rounded cursor-pointer transition-colors",
                        item.status === 'published' ? "bg-green-100 hover:bg-green-200" :
                        item.status === 'scheduled' ? "bg-blue-100 hover:bg-blue-200" :
                        "bg-amber-100 hover:bg-amber-200"
                      )}
                      onClick={() => onItemClick(item)}
                    >
                      <div className="font-medium truncate flex items-center">
                        {getContentTypeIcon(item)}
                        {item.title}
                      </div>
                      <div className="flex items-center text-slate-600 mt-1">
                        {displayDate && format(new Date(displayDate), "HH:mm")}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
